import { useEffect, useState, type DragEvent, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation } from "@apollo/client/react";
import { useSearchParams } from "react-router";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { DeletePinGroupDocument, PinType } from "@/graphql/generated/operations";
import { logApplicationFailure } from "@/lib/user-error";
import { PinGroupDialog } from "./PinGroupDialog";
import {
  SaveCollectionSidebar,
  SaveGroupDeleteConfirm,
  type GroupSelection,
  type SaveGroupLite,
} from "./SaveCollectionTemplate";

// Left navigation of the Pins page: switch pin type (Personen / Beiträge /
// Projekte), then pick a group within that type. Hosts the group CRUD dialogs
// (create / rename / delete) so the page stays focused on the item grid.

export type { GroupSelection };
export type PinGroupLite = SaveGroupLite;

const TYPE_TABS: { type: PinType; label: ReactNode }[] = [
  {
    type: PinType.Person,
    label: (
      <FormattedMessage id="save.pinGroupSidebar.tabPerson" defaultMessage="People" />
    ),
  },
  {
    type: PinType.Post,
    label: (
      <FormattedMessage id="save.pinGroupSidebar.tabPost" defaultMessage="Posts" />
    ),
  },
  {
    type: PinType.Project,
    label: (
      <FormattedMessage id="save.pinGroupSidebar.tabProject" defaultMessage="Projects" />
    ),
  },
];

interface Props {
  embedded?: boolean;
  activeType: PinType;
  onTypeChange: (t: PinType) => void;
  counts: { person: number; post: number; project: number };
  groups: PinGroupLite[];
  activeGroup: GroupSelection;
  onGroupChange: (g: GroupSelection) => void;
  // Refetch groups + counts after a CRUD change.
  onGroupsChanged: () => void;
  // Drag & drop: the page owns the drag state; group rows are drop targets.
  dropTargetId?: string | null;
  dropHandlers?: (groupId: string) => {
    onDragOver: (e: DragEvent) => void;
    onDragLeave: () => void;
    onDrop: (e: DragEvent) => void;
  };
}

export function PinGroupSidebar({
  embedded = false,
  activeType,
  onTypeChange,
  counts,
  groups,
  activeGroup,
  onGroupChange,
  onGroupsChanged,
  dropTargetId,
  dropHandlers,
}: Props) {
  const intl = useIntl();
  // Deep link from the home "+ Sammlung" quick action: open the create dialog
  // on mount (captured once via the initializer), then strip the param.
  const [searchParams, setSearchParams] = useSearchParams();
  const [dialog, setDialog] = useState<
    { mode: "create" } | { mode: "rename"; group: PinGroupLite } | null
  >(() => (searchParams.get("create") === "group" ? { mode: "create" } : null));
  const [deleting, setDeleting] = useState<PinGroupLite | null>(null);
  const [deleteGroup] = useMutation(DeletePinGroupDocument);
  useEffect(() => {
    if (searchParams.has("create")) {
      const next = new URLSearchParams(searchParams);
      next.delete("create");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const typeCount =
    activeType === PinType.Person
      ? counts.person
      : activeType === PinType.Post
        ? counts.post
        : counts.project;

  async function confirmDelete() {
    if (!deleting) return;
    try {
      await deleteGroup({ variables: { input: { id: deleting.id } } });
      if (activeGroup === deleting.id) onGroupChange("all");
      onGroupsChanged();
    } catch {
      logApplicationFailure("save.delete-pin-group");
    } finally {
      setDeleting(null);
    }
  }

  return (
    <>
      <SaveCollectionSidebar
        embedded={embedded}
        topControls={
          embedded ? null : <SegmentedControl
            fullWidth
            value={activeType}
            onChange={onTypeChange}
            options={TYPE_TABS.map((t) => ({ value: t.type, label: t.label }))}
          />
        }
        groups={groups}
        activeGroup={activeGroup}
        allCount={typeCount}
        addLabel={intl.formatMessage({
          id: "save.pinGroupSidebar.addGroup",
          defaultMessage: "+ Group",
        })}
        onGroupChange={onGroupChange}
        onCreateGroup={() => setDialog({ mode: "create" })}
        onRenameGroup={(group) => setDialog({ mode: "rename", group })}
        onDeleteGroup={setDeleting}
        dropTargetId={dropTargetId}
        dropHandlers={dropHandlers}
      />

      <PinGroupDialog
        open={dialog !== null}
        onClose={() => setDialog(null)}
        mode={dialog?.mode ?? "create"}
        pinType={activeType}
        group={dialog?.mode === "rename" ? dialog.group : undefined}
        existingNames={groups.map((g) => g.name)}
        onSaved={onGroupsChanged}
      />

      <SaveGroupDeleteConfirm
        open={deleting !== null}
        title={intl.formatMessage({
          id: "save.pinGroupSidebar.deleteTitle",
          defaultMessage: "Delete group",
        })}
        message={
          deleting
            ? intl.formatMessage(
                {
                  id: "save.pinGroupSidebar.deleteMessage",
                  defaultMessage:
                    "Delete “{name}”? The pins remain, only the grouping is removed.",
                },
                { name: deleting.name },
              )
            : undefined
        }
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}
